import React, { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  XMarkIcon, 
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline'
import { useAuth } from '@/hooks/useAuth'
import { loanService, LoanApplication, UpdateLoanApplicationStatusData } from '@/services/loanService'
import toast from 'react-hot-toast'

interface LoanApplicationApprovalModalProps {
  application: LoanApplication
  onClose: () => void
  onSuccess: () => void
}

const LoanApplicationApprovalModal: React.FC<LoanApplicationApprovalModalProps> = ({ 
  application, 
  onClose, 
  onSuccess 
}) => {
  const { user } = useAuth()
  const [action, setAction] = useState<'approve' | 'reject' | null>(null)
  const [comments, setComments] = useState('')
  const [approvedAmount, setApprovedAmount] = useState(application.requestedAmount.toString())
  const [disbursementMethod, setDisbursementMethod] = useState<'bank_transfer' | 'cash' | 'cheque' | 'mobile_money'>('bank_transfer')
  const [disbursementReference, setDisbursementReference] = useState('')

  const queryClient = useQueryClient()

  // Update application status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async (data: UpdateLoanApplicationStatusData) => {
      return loanService.updateLoanApplicationStatus(application.id, data)
    },
    onSuccess: () => {
      const actionText = action === 'approve' ? 'approved' : 'rejected'
      toast.success(`Loan application ${actionText} successfully`)
      queryClient.invalidateQueries({ queryKey: ['loanApplications'] })
      queryClient.invalidateQueries({ queryKey: ['loans'] })
      onSuccess()
      onClose()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to update application status')
    }
  })

  const getNextStatus = (): string => {
    if (action === 'reject') return 'rejected'
    
    if (user?.role === 'admin') {
      // Admin can approve directly or disburse approved loans
      return application.status === 'approved' ? 'disbursed' : 'approved'
    }
    
    // Determine next status based on current status and user role
    switch (application.status) {
      case 'pending':
        return user?.role === 'manager' ? 'manager_approved' : 'rejected'
      case 'manager_approved':
        return user?.role === 'hr_manager' ? 'hr_approved' : 'rejected'
      case 'hr_approved':
        return user?.role === 'finance_manager' ? 'finance_approved' : 'rejected'
      case 'finance_approved':
        return 'approved'
      case 'approved':
        return 'disbursed'
      default:
        return 'rejected'
    }
  }

  const canApprove = (): boolean => {
    const { role } = user || {}
    const { status } = application
    
    if (role === 'admin') return true
    
    switch (status) {
      case 'pending':
        return role === 'manager'
      case 'manager_approved':
        return role === 'hr_manager'
      case 'hr_approved':
        return role === 'finance_manager'
      case 'finance_approved':
        return ['hr_manager', 'finance_manager'].includes(role || '')
      case 'approved':
        return ['admin', 'finance_manager'].includes(role || '')
      default:
        return false
    }
  }

  const canReject = (): boolean => {
    const { role } = user || {}
    const { status } = application
    
    if (role === 'admin') return true
    
    return ['pending', 'manager_approved', 'hr_approved', 'finance_approved'].includes(status) &&
           ['manager', 'hr_manager', 'finance_manager'].includes(role || '')
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!action) {
      toast.error('Please select an action')
      return
    }

    if (!comments.trim() && action === 'reject') {
      toast.error('Please provide comments for rejection')
      return
    }

    const nextStatus = getNextStatus()
    
    const updateData: UpdateLoanApplicationStatusData = {
      status: nextStatus as any,
      ...(comments.trim() && { comments: comments.trim() })
    }

    // Add approved amount if approving
    if (action === 'approve' && nextStatus === 'approved') {
      const amount = parseFloat(approvedAmount)
      if (amount <= 0 || amount > application.requestedAmount) {
        toast.error('Invalid approved amount')
        return
      }
      updateData.approvedAmount = amount
    }

    // Add disbursement details if disbursing
    if (nextStatus === 'disbursed') {
      if (!disbursementReference.trim()) {
        toast.error('Please provide disbursement reference')
        return
      }
      updateData.disbursementMethod = disbursementMethod
      updateData.disbursementReference = disbursementReference.trim()
    }

    updateStatusMutation.mutate(updateData)
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'manager_approved': return 'bg-blue-100 text-blue-800'
      case 'hr_approved': return 'bg-blue-100 text-blue-800'
      case 'finance_approved': return 'bg-blue-100 text-blue-800'
      case 'approved': return 'bg-green-100 text-green-800'
      case 'disbursed': return 'bg-green-100 text-green-800'
      case 'rejected': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between pb-3 border-b">
          <h3 className="text-lg font-medium text-gray-900">
            Review Loan Application
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="mt-4">
          {/* Application Details */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h4 className="text-md font-medium text-gray-900 mb-3">Application Details</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-gray-500">Employee:</span>
                  <p className="text-sm text-gray-900">
                    {application.employee.firstName} {application.employee.lastName} ({application.employee.employeeNumber})
                  </p>
                  <p className="text-xs text-gray-500">
                    {application.employee.department} • {application.employee.position}
                  </p>
                </div>
                
                <div>
                  <span className="text-sm font-medium text-gray-500">Loan Type:</span>
                  <p className="text-sm text-gray-900">{application.loanType.name}</p>
                  <p className="text-xs text-gray-500">
                    {application.loanType.interestRate}% interest • Processing Fee: {formatCurrency(application.loanType.processingFee)}
                  </p>
                </div>

                <div>
                  <span className="text-sm font-medium text-gray-500">Status:</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(application.status)}`}>
                    {application.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-gray-500">Requested Amount:</span>
                  <p className="text-sm font-semibold text-gray-900">{formatCurrency(application.requestedAmount)}</p>
                </div>

                <div>
                  <span className="text-sm font-medium text-gray-500">Loan Term:</span>
                  <p className="text-sm text-gray-900">{application.loanTermMonths} months</p>
                </div>

                <div>
                  <span className="text-sm font-medium text-gray-500">Applied On:</span>
                  <p className="text-sm text-gray-900">{formatDate(application.createdAt)}</p>
                </div>
              </div>
            </div>

            <div className="mt-4">
              <span className="text-sm font-medium text-gray-500">Purpose:</span>
              <p className="text-sm text-gray-900 mt-1">{application.purpose}</p>
            </div>

            {application.guarantorEmployee && (
              <div className="mt-3">
                <span className="text-sm font-medium text-gray-500">Guarantor:</span>
                <p className="text-sm text-gray-900">
                  {application.guarantorEmployee.firstName} {application.guarantorEmployee.lastName} ({application.guarantorEmployee.employeeNumber})
                </p>
              </div>
            )}

            {application.collateralDescription && (
              <div className="mt-3">
                <span className="text-sm font-medium text-gray-500">Collateral:</span>
                <p className="text-sm text-gray-900">{application.collateralDescription}</p>
              </div>
            )}
          </div>

          {/* Action Form */}
          <form onSubmit={handleSubmit}>
            {/* Action Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Action
              </label>
              <div className="flex space-x-4">
                {canApprove() && (
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="action"
                      value="approve"
                      onChange={() => setAction('approve')}
                      className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      <CheckCircleIcon className="h-4 w-4 inline mr-1 text-green-600" />
                      Approve
                    </span>
                  </label>
                )}
                
                {canReject() && (
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="action"
                      value="reject"
                      onChange={() => setAction('reject')}
                      className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      <XCircleIcon className="h-4 w-4 inline mr-1 text-red-600" />
                      Reject
                    </span>
                  </label>
                )}
              </div>
            </div>

            {/* Approved Amount (for final approval) */}
            {action === 'approve' && getNextStatus() === 'approved' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Approved Amount (USD)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max={application.requestedAmount}
                  value={approvedAmount}
                  onChange={(e) => setApprovedAmount(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Maximum: {formatCurrency(application.requestedAmount)}
                </p>
              </div>
            )}

            {/* Disbursement Details (for disbursement) */}
            {action === 'approve' && getNextStatus() === 'disbursed' && (
              <div className="space-y-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Disbursement Method
                  </label>
                  <select
                    value={disbursementMethod}
                    onChange={(e) => setDisbursementMethod(e.target.value as any)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    required
                  >
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="cash">Cash</option>
                    <option value="cheque">Cheque</option>
                    <option value="mobile_money">Mobile Money</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Disbursement Reference
                  </label>
                  <input
                    type="text"
                    value={disbursementReference}
                    onChange={(e) => setDisbursementReference(e.target.value)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    placeholder="Transaction ID, Cheque number, etc."
                    required
                  />
                </div>
              </div>
            )}

            {/* Comments */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Comments {action === 'reject' && <span className="text-red-500">*</span>}
              </label>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                rows={3}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                placeholder={action === 'reject' ? 'Please provide reason for rejection...' : 'Optional comments...'}
                required={action === 'reject'}
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={updateStatusMutation.isPending || !action}
                className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md disabled:opacity-50 ${
                  action === 'approve' 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {updateStatusMutation.isPending 
                  ? 'Processing...' 
                  : action === 'approve' 
                    ? getNextStatus() === 'disbursed' ? 'Disburse Loan' : 'Approve Application'
                    : 'Reject Application'
                }
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default LoanApplicationApprovalModal